import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import {
  GridRowModes,
  GridToolbarContainer,
  GridActionsCellItem,
  useGridApiRef,
  GridBooleanCell,
} from '@mui/x-data-grid-premium';

import { dataGridPremiumColumns } from '../../utils/helper';
import {
  DataGridIdentifiers,
  FormErrorUI,
  AddButtonUi,
  AutoCompleteSelectUI,
  DatagridPremiumUi,
  InputUI,
  CheckboxUI,
  DatePickerUI,
} from '../Interface';
import { formatCurrency } from '../../utils/currencyHelper';
import dayjs from 'dayjs';
import useGenerateJokerCols from '../../hooks/useGenerateJokerCols';

const EditToolbar = (props) => {
  const { disabled, push, defEmployeeLine } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    const newData = {
      ...defEmployeeLine,
      id: uuidv4(),
    };

    await push(newData);

    await validateField(`employeeLine.${values.employeeLine.length ?? 0}.description`);
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const EmployeeLine = ({ disabled, defEmployeeLine, jokerLineFields }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const getValueIndex = (values, id) => {
    return values.employeeLine.findIndex((rc) => rc.id === id);
  };

  const jokerColumns = useGenerateJokerCols(jokerLineFields, 'employeeLine', getValueIndex);

  const columns = (remove) =>
    dataGridPremiumColumns([
      {
        field: 'description',
        headerName: 'Megjegyzés',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`employeeLine.${valueIndex}.description`}
              as={InputUI}
              variant="standard"
              fullWidth
              helperText={<FormErrorUI message={errors?.employeeLine?.[valueIndex]?.description} />}
              onChange={(e) => {
                setFieldValue(`employeeLine.${valueIndex}.description`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`employeeLine.${valueIndex}.description`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
      ...jokerColumns,
    ]);

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="employeeLine">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.EmployeeLine}
                height={480}
                columns={columns(remove)}
                rows={values?.employeeLine ?? []}
                editMode="row"
                slots={{
                  toolbar: EditToolbar,
                }}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defEmployeeLine,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                headerFilters={false}
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  if (disabled) return;

                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default EmployeeLine;
